/**
 * web/src/projection/types.ts —— 投影 DTO 类型（TS）
 *
 * 形状唯一源＝./dto.schema.json（main 面，本件只读消费；**不得推断、不得扩字段**）。
 * 本文件是 dto.schema.json 的手工 TS 镜像：每个 $defs 一型，字段一一对应，
 * 校验规则（required/enum/const/additionalProperties:false）落为类型层约束与
 * 运行时守卫（见 ./guards.ts）。判据：web_contract_projection_only（只消费投影）。
 *
 * 对应后端只读装配（只读参照，不在本树）：
 *   src/sononux/proj/views.py         assemble_view  → ViewAssembly
 *   src/sononux/proj/render_desc.py   render_desc    → RenderDesc
 *   src/sononux/proj/approval_view.py approval_card  → ApprovalCard
 */

// ── $defs.SlotPointer ────────────────────────────────────────────────
// required: [kind, pointer]; kind ∈ {history, rows}; additionalProperties: false
export interface SlotPointer {
  kind: SlotPointerKind;
  pointer: string;
}
export type SlotPointerKind = "history" | "rows";

// ── $defs.ViewFact ───────────────────────────────────────────────────
// normalize_fact(fact) 产物：结构化事实（additionalProperties: true → 索引签名）。
// 字段集按 render_desc.fields 声明（见 FIELD_KEYS）；此处的 key 列表只是
// 声明内字段的具名视图，事实可含更多声明允许的键，消费一律经 pickDeclaredFields。
export interface ViewFact {
  [key: string]: unknown;
}

// ── $defs.ViewAssembly ───────────────────────────────────────────────
// required: [fact, slots]; additionalProperties: false
export interface ViewAssembly {
  fact: ViewFact;
  slots: SlotPointer[];
}

// ── render_desc.fields 枚举（schema 内联 enum，封闭集＝九键＋图结构三键）──
// 九键＝事实键；nodes/edges/waves＝B10 读面冻结件 §3 闭集扩展（值＝不透明引用串，
// 不承载取数语义）。镜像跟随唯一源 dto.schema.json，只增不改。
export const FIELD_KEYS = [
  "key",
  "action",
  "resource",
  "scope",
  "args_digest",
  "risk_tags",
  "actor",
  "step",
  "expires_at",
  "attachments",   // ★F3c：附件引用位（值＝file_id 空格分隔串；与内核 FACT_KEYS 同源随动）
  "nodes",
  "edges",
  "waves",
] as const;
export type FieldKey = (typeof FIELD_KEYS)[number];

// ── $defs.RenderDesc ─────────────────────────────────────────────────
// required: [title_key, fields, pointers]; additionalProperties: false;
// fields 项 ∈ 九键封闭枚举（schema inline enum，与 FIELD_KEYS 一致）
export interface RenderDesc {
  title_key: string;
  fields: FieldKey[];
  pointers: SlotPointer[];
}

// ── $defs.ApprovalCard ───────────────────────────────────────────────
// required: [surface]; surface 常量 "approval"; additionalProperties: true
//（唯一呈现面；同意/拒绝动作必须落账——落账本身走写面，归 U-924/W 面，不在本件）
export interface ApprovalCard {
  surface: "approval";
  [key: string]: unknown;
}

// ── $defs.EventFrame ────────────────────────────────────────────────
// required: [id, name]; additionalProperties: false
// name ⊆ decl/components.yaml ∪ decl/baseline.yaml 声明清单（现行 6 条，只增不改——前端不自造事件名）。
// 声明清单由波0 契约冻结（CONTRACT.md §3）；此处以联合类型承接，新增事件随契约升版。
// 🔴 真源对拍（实读后端声明件、逐条求等；不等即红）＝tests/frontend.gaps.c7.events.test.ts ①b。
export const DECLARED_EVENT_NAMES = [
  "crm.engagement.contacted",
  "crm.order.submitted",
  "platform.approval_decided",
  "platform.view.declared",
  "platform.ws.command_accepted",
  // 20261007 补（**只增不改**）：后端 `decl/baseline.yaml` ★S6c 类D·6 已声明（平台面首个非 `platform.`
  // 前缀名——名逐字沿用定案 82「事件账含 `context.assembled`＝不可丢，漏一条即红」）；前端镜像滞后一条
  // ⇒ ①b 真源对拍长期红；本次按「真源＝后端声明件、前端只镜像」补齐。
  "context.assembled",
  // 20261011 追加（**只增不改**；★SESS1／账行 `L-PF-LLM-1`）：run 触发事实（回合起）——后端
  // `decl/baseline.yaml` 已声明（`kernel/edge/triggers.fire` 受理即落 `wf.event` 一条）；
  // 按「真源＝后端声明件、前端只镜像」同步补齐，否则 ①b 真源对拍红。
  "platform.run.triggered",
] as const;
export type DeclaredEventName = (typeof DECLARED_EVENT_NAMES)[number];
export interface EventFrame {
  id: string;
  name: DeclaredEventName;
  payload?: ProjectionDto;
}

// ── $defs.SnapshotFrame ──────────────────────────────────────────────
// required: [cursor, views]; additionalProperties: false（首帧与断线回放）
export interface SnapshotFrame {
  cursor: string;
  views: ViewAssembly[];
}

// ── $defs.command_ack ───────────────────────────────────────────────
// **第三帧型**（CONTRACT §3 v1.5；v1.7 补 `client_id`）：**上行命令回执**（下行方向）。
// required: [id, kind, status]; status ∈ {accepted, rejected, error}；
// 键闭集（＝后端 `components/entry_session/command_ingress.py::REPLY_KEYS`）：
//   id／kind／status／reply?／event?／error?／client_id?（None/缺值不落键：缺即缺，不补造）。
// 🔴 **命令是请求不是事件**：`kind ∈ {command, steer, stop, error}` ≠ 事件词域，
//    本帧 **不入** `DECLARED_EVENT_NAMES`（不为命令造词域）。
// 实况逐字（后端已上线并实测；v1.7 起回执恒回带 `client_id`，无上行关联键 ⇒ 空串）：
//   {"id":"cmd-2","kind":"command","status":"accepted","client_id":"cmd-local-1",
//    "reply":{"accepted":true,"kind":"command","text":"上线验证"}}
export type CommandAckStatus = "accepted" | "rejected" | "error";
export interface CommandAckFrame {
  id: string;
  /** 请求名（闭集同上行信封）／`"error"`（拒绝回执的 kind，后端 `_reject` 口径） */
  kind: string;
  status: CommandAckStatus;
  /** 受理回执正文（`{accepted, kind, text?, session?/http_status?}`；缺即缺） */
  reply?: Record<string, unknown>;
  /** 落账结果（`{http_status, body}`；无目标事件名 ⇒ 不出现） */
  event?: Record<string, unknown>;
  /** 错误回执（`{code, message}`；仅 rejected／error） */
  error?: Record<string, unknown>;
  /**
   * ★A-031（v1.7）：上行 `client_id` **原值回带**（前端按它精确落位；空串／缺＝无关联）。
   * **非身份**（仅关联用）；无对应 pending 行 ⇒ 不造行（保持现纪律）。
   */
  client_id?: string;
}

// ── 投影 DTO 三型联合（EventFrame.payload 的合法形状） ────────────────
export type ProjectionDto = ViewAssembly | RenderDesc | ApprovalCard;

// 通道帧**三型**联合（CONTRACT.md §3 v1.5：event ／ snapshot ／ command_ack）
export type ChannelFrame = EventFrame | SnapshotFrame | CommandAckFrame;

/** 帧判别（唯一处；`views` 优先 ⇒ snapshot，其次 `name` ⇒ event，其余按 `status` 判 command_ack） */
export function frameKindOf(frame: ChannelFrame): "snapshot" | "event" | "command_ack" {
  if ("views" in frame) return "snapshot";
  if ("name" in frame) return "event";
  return "command_ack";
}
